<template>
  <div
    v-if="facebookSignInEnabled"
    class="field is-grouped is-grouped-centered"
  >
    <div class="control auth-ui-button-and-spinner-container">
      <button
        class="auth-ui-fb-button"
        @click.prevent="facebookSignIn()"
      >
        {{ $t(buttonTextVariable) }}
      </button>
    </div>
  </div>
</template>

<script>
import Request from '@/tools/Request';
/* ============
   * Facebook Sign In Component
   * ============
   *
   * Sign in with facebook button that - surprise, surprise - enables the
   * user to log in with Facebook.
   */

export default {
  name: 'FacebookSignIn',

  props: {
    buttonPressed: {
      type: Boolean,
      default: false,
      required: true,
    },
    buttonTextVariable: {
      type: String,
      default: 'auth.fbSignInButton',
      required: false,
    },
    toggleButtonPressed: {
      type: Function,
      default: () => {},
      required: true,
    },
    showErrorMessage: {
      type: Function,
      default: () => {},
      required: true,
    },
  },

  data() {
    return {
      forcePermission: false, // used in FB sign in
    };
  },

  computed: {
    facebookSignInEnabled() {
      return process.env.VUE_APP_ENABLE_FACEBOOK_SIGN_IN === 'true';
    },
  },

  methods: {
    async facebookSignIn() {
      if (!this.buttonPressed) {
        this.toggleButtonPressed(true);
        this.showErrorMessage(false);
        this.toggleShowSpinner(true);

        const options = this.getOptions();

        FB.login(async (fbResponse) => { // eslint-disable-line no-undef
          if (fbResponse.status === 'unknown' || fbResponse.status === 'not_authorized') {
            // connected = logged in to both FB and Puheet
            // not_authorized = logged in to FB but not to Puheet
            // unknown = not logged in to FB (don't know Puheet logged in status)
            this.showErrorMessage(true, this.$t('auth.fbGenericErrorMessage'), true);
            this.toggleButtonPressed(false);
            this.toggleShowSpinner(false);
            return;
          }

          await this.authenticateWithPuheetPlatform(fbResponse.authResponse);
        },
        options);
      }
    },

    getOptions() {
      const options = {
        scope: 'email',
        return_scopes: true,
      };

      if (this.forcePermission) {
        options.auth_type = 'rerequest'; // used to rerequest a declined permission
      }

      return options;
    },

    async authenticateWithPuheetPlatform(authResponse) {
      const backendResponse = await this.puheetFbSignIn(authResponse);

      if (backendResponse.error) {
        this.toggleButtonPressed(false);
        this.toggleShowSpinner(false);

        if (backendResponse.code === 'MISSING_SCOPE_EMAIL') {
          this.showErrorMessage(true, this.$t('auth.fbScopePermissionErrorMessage'), true);
        } else {
          this.showErrorMessage(true, this.$t('auth.fbGenericErrorMessage'), true);
        }
        this.forcePermission = true;
      } else {
        this.toggleShowSpinner(false);
        this.handleSignInSuccess(backendResponse);
      }
    },

    async puheetFbSignIn(authObject) {
      try {
        return await Request.submit(
          'POST',
          `${process.env.VUE_APP_BACKEND_URL}/fbsignin`,
          { auth: authObject },
        );
      } catch (error) {
        return error;
      }
    },

    handleSignInSuccess(response) {
      if (response.signedIn) {
        const target = this.$store.state.route.query.target;
        if (target && target.length) {
          window.location.replace(decodeURI(target));
        } else {
          window.location.replace(`${process.env.VUE_APP_AFTER_SIGN_IN_URL}`);
        }
      } else {
        // This should never happen, as the server returns "signedIn" in the response,
        // unless the response contains an error which is then handled in the catch block
        this.toggleButtonPressed(false);
        this.showErrorMessage(true, this.$t('auth.signInServerErrorMessage'), true);
      }
    },

    /**
     * Toggles the app-wide spinner state in Vuex store.
     */
    toggleShowSpinner(isVisible = true) {
      this.$store.dispatch('general/setShowSpinner', isVisible);
    },
  },
};
</script>
